/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export const Operation = {
  Require: "require",
  RequireAny: "require_any",
  Filter: "filter",
} as const;
export type Operation = ClosedEnum<typeof Operation>;

export const Outcome = {
  Allow: "allow",
  Deny: "deny",
  Error: "error",
} as const;
export type Outcome = ClosedEnum<typeof Outcome>;

/**
 * Kind of principal.
 */
export const PrincipalType = {
  User: "user",
  ApiKey: "api_key",
  Assistant: "assistant",
  Agent: "agent",
  Workload: "workload",
} as const;
/**
 * Kind of principal.
 */
export type PrincipalType = ClosedEnum<typeof PrincipalType>;

export const ChallengeBucketReason = {
  GrantMatched: "grant_matched",
  NoGrants: "no_grants",
  ScopeUnsatisfied: "scope_unsatisfied",
  DenyGrant: "deny_grant",
  InvalidCheck: "invalid_check",
  RbacSkippedApikey: "rbac_skipped_apikey",
  DevOverride: "dev_override",
} as const;
export type ChallengeBucketReason = ClosedEnum<typeof ChallengeBucketReason>;

/**
 * How the bucket was resolved.
 */
export const ResolutionType = {
  RoleAssigned: "role_assigned",
  Dismissed: "dismissed",
} as const;
/**
 * How the bucket was resolved.
 */
export type ResolutionType = ClosedEnum<typeof ResolutionType>;

/**
 * A group of consecutive challenges with the same dimensions that occurred within a 10-minute window.
 */
export type ChallengeBucket = {
  /**
   * Number of individual challenges in this bucket.
   */
  challengeCount: number;
  /**
   * IDs of all challenges in this bucket.
   */
  challengeIds: Array<string>;
  /**
   * Total grants evaluated.
   */
  evaluatedGrantCount: number;
  /**
   * Timestamp of the earliest challenge in the bucket.
   */
  firstSeen: Date;
  /**
   * ID of the most recent challenge in the bucket.
   */
  id: string;
  /**
   * Timestamp of the most recent challenge in the bucket.
   */
  lastSeen: Date;
  /**
   * Number of grants that matched.
   */
  matchedGrantCount: number;
  operation: Operation;
  /**
   * Organization the principal was acting in.
   */
  organizationId: string;
  outcome: Outcome;
  /**
   * User avatar URL when available.
   */
  photoUrl?: string | undefined;
  /**
   * Kind of principal.
   */
  principalType: PrincipalType;
  /**
   * Principal URN e.g. user:<uuid> or api_key:<id>.
   */
  principalUrn: string;
  /**
   * Project scope (empty for org-level checks).
   */
  projectId?: string | undefined;
  reason: ChallengeBucketReason;
  /**
   * Role slug assigned (when resolution_type=role_assigned).
   */
  resolutionRoleSlug?: string | undefined;
  /**
   * How the bucket was resolved.
   */
  resolutionType?: ResolutionType | undefined;
  /**
   * When the bucket was resolved by an admin.
   */
  resolvedAt?: Date | undefined;
  /**
   * URN of the admin who resolved.
   */
  resolvedBy?: string | undefined;
  /**
   * Resource ID of the check.
   */
  resourceId?: string | undefined;
  /**
   * Resource kind of the check.
   */
  resourceKind?: string | undefined;
  /**
   * Roles the principal had loaded.
   */
  roleSlugs: Array<string>;
  /**
   * Scope that was checked.
   */
  scope: string;
  /**
   * Email when available.
   */
  userEmail?: string | undefined;
};

/** @internal */
export const Operation$inboundSchema: z.ZodMiniEnum<typeof Operation> = z.enum(
  Operation,
);

/** @internal */
export const Outcome$inboundSchema: z.ZodMiniEnum<typeof Outcome> = z.enum(
  Outcome,
);

/** @internal */
export const PrincipalType$inboundSchema: z.ZodMiniEnum<typeof PrincipalType> =
  z.enum(PrincipalType);

/** @internal */
export const ChallengeBucketReason$inboundSchema: z.ZodMiniEnum<
  typeof ChallengeBucketReason
> = z.enum(ChallengeBucketReason);

/** @internal */
export const ResolutionType$inboundSchema: z.ZodMiniEnum<
  typeof ResolutionType
> = z.enum(ResolutionType);

/** @internal */
export const ChallengeBucket$inboundSchema: z.ZodMiniType<
  ChallengeBucket,
  unknown
> = z.pipe(
  z.object({
    challenge_count: z.int(),
    challenge_ids: z.array(z.string()),
    evaluated_grant_count: z.int(),
    first_seen: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    id: z.string(),
    last_seen: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    matched_grant_count: z.int(),
    operation: Operation$inboundSchema,
    organization_id: z.string(),
    outcome: Outcome$inboundSchema,
    photo_url: z.optional(z.string()),
    principal_type: PrincipalType$inboundSchema,
    principal_urn: z.string(),
    project_id: z.optional(z.string()),
    reason: ChallengeBucketReason$inboundSchema,
    resolution_role_slug: z.optional(z.string()),
    resolution_type: z.optional(ResolutionType$inboundSchema),
    resolved_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    resolved_by: z.optional(z.string()),
    resource_id: z.optional(z.string()),
    resource_kind: z.optional(z.string()),
    role_slugs: z.array(z.string()),
    scope: z.string(),
    user_email: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "challenge_count": "challengeCount",
      "challenge_ids": "challengeIds",
      "evaluated_grant_count": "evaluatedGrantCount",
      "first_seen": "firstSeen",
      "last_seen": "lastSeen",
      "matched_grant_count": "matchedGrantCount",
      "organization_id": "organizationId",
      "photo_url": "photoUrl",
      "principal_type": "principalType",
      "principal_urn": "principalUrn",
      "project_id": "projectId",
      "resolution_role_slug": "resolutionRoleSlug",
      "resolution_type": "resolutionType",
      "resolved_at": "resolvedAt",
      "resolved_by": "resolvedBy",
      "resource_id": "resourceId",
      "resource_kind": "resourceKind",
      "role_slugs": "roleSlugs",
      "user_email": "userEmail",
    });
  }),
);

export function challengeBucketFromJSON(
  jsonString: string,
): SafeParseResult<ChallengeBucket, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ChallengeBucket$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ChallengeBucket' from JSON`,
  );
}
